// ScaleBar.jsx
// This file is a component that renders a scale bar on the viewer. It is used to display the scale of the image in the viewer. It is used in the Viewer component.

// Copyright HS-Analysis GmbH, 2019
// Author: Sebastian Murgul, Viktor Eberhard

// Framework imports
import React from "react";
import PropTypes from "prop-types";

// External imports
import classNames from "classnames";
import Draggable from "react-draggable";

// Material-UI imports
import withStyles from "@mui/styles/withStyles";

// HSA imports
import { ValidationType, validateType } from "../../common/utils/Utils";

const MAX_SCALE_WIDTH = 200;
const MAX_SCALE_BAR_WIDTH = MAX_SCALE_WIDTH * 1.5;
const SCALE_UNIT_LABELS = [
  "km",
  "m",
  "cm",
  "mm",
  "\u00B5m",
  "nm",
  "pm",
  "fm",
  "am",
  "zm",
];
const SCALE_UNITS = [3, 0, -2, -3, -6, -9, -12, -15, -18, -21];

const styles = {
  root: {
    width: MAX_SCALE_BAR_WIDTH,
    height: 30,

    position: "absolute",
    bottom: 4,
    right: 6,
    color: "#ffffff",

    textAlign: "center",
    padding: 4,
    cursor: "grab",
  },
};

/**
 * Basic Units to measure length.
 * Conversion factor is always to meter.
 */
class LengthUnit {
  constructor(name, factor, symbol) {
    // Input validation
    validateType("name", name, ValidationType.String);
    validateType("factor", factor, ValidationType.Number);
    validateType("symbol", symbol, ValidationType.String);

    this.name = name;
    this.factor = factor;
    this.symbol = symbol;
  }
}

// All available units to measure length with conversion factors to meter
const availableUnits = {
  pixel: new LengthUnit("pixel", 1, "px"),
  femtometer: new LengthUnit("femtometer", 10 ** -15, "fm"),
  picometer: new LengthUnit("picometer", 10 ** -12, "pm"),
  angstrom: new LengthUnit("angstrom", 10 ** -10, "Å"),
  nanometer: new LengthUnit("nanometer", 10 ** -9, "nm"),
  micrometer: new LengthUnit("micrometer", 10 ** -6, "\u00B5m"),
  millimeter: new LengthUnit("millimeter", 10 ** -3, "mm"),
  centimeter: new LengthUnit("centimeter", 10 ** -2, "cm"),
  meter: new LengthUnit("meter", 1, "m"),
  kilometer: new LengthUnit("kilometer", 10 ** 3, "km"),
  inch: new LengthUnit("inch", 0.0254, "in"),
  foot: new LengthUnit("foot", 0.3048, "ft"),
  yard: new LengthUnit("yard", 0.9144, "yd"),
  mile: new LengthUnit("mile", 1609.34, "mi"),
  "nautical mile": new LengthUnit("nautical mile", 1852, "nmi"),
  "astronomical unit": new LengthUnit("astronomical unit", 1.496e11, "au"),
  "light year": new LengthUnit("light year", 9.461e15, "ly"),
  parsec: new LengthUnit("parsec", 3.086e16, "pc"),
};

// Common unit mappings to standardize unit names
const commonUnitMappings = {
  nm: availableUnits["nanometer"],
  µm: availableUnits["micrometer"],
  um: availableUnits["micrometer"],
  mm: availableUnits["millimeter"],
  cm: availableUnits["centimeter"],
  m: availableUnits["meter"],
  metre: availableUnits["meter"],
  km: availableUnits["kilometer"],
  in: availableUnits["inch"],
  ft: availableUnits["foot"],
  yd: availableUnits["yard"],
  mi: availableUnits["mile"],
  nmi: availableUnits["nautical mile"],
  au: availableUnits["astronomical unit"],
  ly: availableUnits["light year"],
  pc: availableUnits["parsec"],
};

/**
 * Retrieves the unit object for a given unit string. Default to pixel if no unit is found.
 * @param {string} unitString Unit string to get the unit for.
 * @returns {LengthUnit} The unit object for the given unit string.
 */
function getUnit(unitString) {
  validateType("unitString", unitString, ValidationType.String);

  if (unitString in availableUnits) {
    return availableUnits[unitString];
  }

  if (unitString in commonUnitMappings) {
    return commonUnitMappings[unitString];
  }

  return availableUnits["pixel"];
}

/**
 * computing data to let renderer render the scalebar as a canvas element, so it can be printed with screenshot tool
 */
class ScaleBar extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      grabbing: false,
      x: 0,
      y: 0,
      newWidth: 0,
    };
    // cache last payload sent to parent to avoid redundant updates
    this._lastSent = null;
  }

  componentDidMount() {
    this.updateScaleBarData();
  }

  componentDidUpdate(prevProps, prevState) {
    // Only recompute when zoom changes, ome reference changes, or position changes
    if (
      prevProps.zoom !== this.props.zoom ||
      prevProps.ome !== this.props.ome ||
      prevState.x !== this.state.x ||
      prevState.y !== this.state.y
    ) {
      this.updateScaleBarData();
    }
  }

  updateScaleBarData() {
    const { ome, zoom } = this.props;

    // Hide if no scale information is given
    if (!ome || !ome.physicalSizeX) return;
    const unit = getUnit(ome.physicalSizeXUnit);
    if (unit === availableUnits["pixel"]) return;

    // Calculate width represented by scalebar width at full scalebar width
    const scalebarMeters = (ome.physicalSizeX * MAX_SCALE_WIDTH) / zoom;
    const exponent = parseFloat(scalebarMeters.toExponential().split("e")[1]);

    // Find best fitting exponent
    let unitExponent = 0;
    let unitExponentIndex = 0;
    for (
      unitExponentIndex = 0;
      unitExponentIndex < SCALE_UNITS.length;
      unitExponentIndex++
    ) {
      if (SCALE_UNITS[unitExponentIndex] <= exponent) {
        unitExponent = SCALE_UNITS[unitExponentIndex];
        break;
      }
    }

    let mantissa = scalebarMeters / Math.pow(10, unitExponent);
    let roundedMantissa = mantissa;

    do {
      if (mantissa < 1.5) {
        roundedMantissa = 1;
      } else if (mantissa < 3.5) {
        roundedMantissa = 2;
      } else if (mantissa < 7.5) {
        roundedMantissa = 5;
      } else if (mantissa < 15) {
        roundedMantissa = 10;
      } else if (mantissa < 35) {
        roundedMantissa = 20;
      } else if (mantissa < 75) {
        roundedMantissa = 50;
      } else if (mantissa < 150) {
        roundedMantissa = 100;
      } else if (mantissa < 350) {
        roundedMantissa = 200;
      } else if (mantissa < 750) {
        roundedMantissa = 500;
      } else {
        // mantissa >= 1000 -> switch to next larger unit
        roundedMantissa = 1;
        unitExponentIndex--;
        unitExponent = SCALE_UNITS[unitExponentIndex];
        mantissa = scalebarMeters / Math.pow(10, unitExponent);
      }
    } while (unitExponentIndex > 0 && mantissa > 1000);

    // Round width to integer px to avoid float jitter that can cause loops
    const newWidth = Math.round((MAX_SCALE_WIDTH * roundedMantissa) / mantissa);
    const fluorescence = ome.channels[0].type !== "brightfield";
    const payload = {
      x: this.state.x,
      y: this.state.y,
      width: newWidth,
      label: roundedMantissa + " " + SCALE_UNIT_LABELS[unitExponentIndex],
      color: fluorescence ? "#fff" : "#000",
      maxScaleWidth: MAX_SCALE_BAR_WIDTH,
    };

    // Only notify parent when something actually changed
    if (
      !this._lastSent ||
      this._lastSent.width !== payload.width ||
      this._lastSent.label !== payload.label ||
      this._lastSent.x !== payload.x ||
      this._lastSent.y !== payload.y
    ) {
      this._lastSent = payload;
      if (typeof this.props.setScaleBarData === "function") {
        this.props.setScaleBarData(payload);
      }
    }
  }

  render() {
    const { classes, ome } = this.props;
    const { grabbing } = this.state;

    // Hide if no scale information is given
    if (!ome.physicalSizeX) return null;
    const unit = getUnit(ome.physicalSizeXUnit);
    if (unit === availableUnits["pixel"]) return null;

    return (
      <Draggable
        onStart={(e) => {
          this.parentObj = e.target.offsetParent.getBoundingClientRect();
          this.setState({ grabbing: true });
        }}
        onStop={() => this.setState({ grabbing: false })}
        onDrag={(e) => {
          let rect = e.target.getBoundingClientRect();
          if (this.parentObj.right !== rect.right) {
            this.setState({
              x: rect.right - this.parentObj.right,
              y: rect.bottom - this.parentObj.bottom,
            });
          }
        }}
      >
        <div
          className={classNames(classes.root, grabbing && classes.grabbing)}
          style={{
            // This is a visual hint only; actual drawing happens on canvas in parent
            width: this._lastSent?.width || MAX_SCALE_WIDTH,
            right:
              "calc(105px - " +
              (this._lastSent?.width || MAX_SCALE_WIDTH) / 2 +
              "px)",
            pointerEvents: this.props.pointerEvents ? "all" : "none",
          }}
        ></div>
      </Draggable>
    );
  }
}

// define the component's interface
ScaleBar.propTypes = {
  classes: PropTypes.object.isRequired,
  ome: PropTypes.object,
  zoom: PropTypes.number,
  pointerEvents: PropTypes.bool,
  setScaleBarData: PropTypes.func,
};

export default withStyles(styles)(ScaleBar);
